import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AXIS_CONTROLS,
  BUTTON_CONTROLS,
  type AxisKey,
  type ButtonKey,
  type ControllerLayout,
  type MappingProfile,
  createProfile,
  loadProfiles,
  mappedAxisValue,
  mappedButtonValue,
  resolveProfile,
  saveProfiles,
} from "./lib/gamepad-mapping";
import { CONTROLLER_ARTWORK } from "./lib/controller-artwork";
import {
  CONTROLLER_VISUAL_PRESETS,
  loadVisualLayoutOverrides,
  saveVisualLayoutOverrides,
  type VisualCluster,
  type VisualLayoutOverrides,
  type VisualPoint,
} from "./lib/controller-layouts";

interface ButtonSnapshot {
  pressed: boolean;
  touched: boolean;
  value: number;
}

interface PadSnapshot {
  index: number;
  id: string;
  mapping: string;
  connected: boolean;
  timestamp: number;
  buttons: ButtonSnapshot[];
  axes: number[];
  haptics: boolean;
  demo?: boolean;
}

interface InputEventItem {
  id: number;
  time: string;
  input: string;
  action: string;
  value: string;
}

type CaptureTarget = { kind: "button"; key: ButtonKey } | { kind: "axis"; key: AxisKey };
type AppScreen = "live" | "raw" | "mapping";

const cloneProfile = (profile: MappingProfile): MappingProfile => structuredClone(profile);

function snapshotGamepads(): PadSnapshot[] {
  if (!("getGamepads" in navigator)) return [];
  return Array.from(navigator.getGamepads())
    .filter((gamepad): gamepad is Gamepad => gamepad !== null)
    .map((gamepad) => ({
      index: gamepad.index,
      id: gamepad.id,
      mapping: gamepad.mapping,
      connected: gamepad.connected,
      timestamp: gamepad.timestamp,
      buttons: gamepad.buttons.map((button) => ({
        pressed: button.pressed,
        touched: button.touched,
        value: button.value,
      })),
      axes: [...gamepad.axes],
      haptics: "vibrationActuator" in gamepad,
    }));
}

function useGamepads() {
  const supported = typeof navigator !== "undefined" && "getGamepads" in navigator;
  const [gamepads, setGamepads] = useState<PadSnapshot[]>([]);

  useEffect(() => {
    if (!supported) return;
    let frame = 0;
    let lastUpdate = 0;

    const update = (time: number) => {
      if (time - lastUpdate > 32) {
        setGamepads(snapshotGamepads());
        lastUpdate = time;
      }
      frame = requestAnimationFrame(update);
    };

    const refresh = () => setGamepads(snapshotGamepads());
    window.addEventListener("gamepadconnected", refresh);
    window.addEventListener("gamepaddisconnected", refresh);
    refresh();
    frame = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("gamepadconnected", refresh);
      window.removeEventListener("gamepaddisconnected", refresh);
    };
  }, [supported]);

  return { gamepads, supported };
}

function useDemoPad(active: boolean): PadSnapshot | null {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!active) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), 40);
    return () => window.clearInterval(timer);
  }, [active]);

  if (!active) return null;
  const time = tick / 25;
  const activeButton = Math.floor(time / 0.75) % 17;
  const buttons = Array.from({ length: 21 }, (_, index) => ({
    pressed: index === activeButton,
    touched: index === activeButton,
    value: index === activeButton ? 1 : 0,
  }));
  const axes = [
    Math.sin(time * 1.25) * 0.82,
    Math.cos(time * 1.05) * 0.72,
    Math.cos(time * 0.72) * 0.64,
    Math.sin(time * 0.88) * 0.78,
    0,
    0,
  ];

  return {
    index: 99,
    id: "8BitDo Ultimate 2 Wireless — demo",
    mapping: "standard",
    connected: true,
    timestamp: tick,
    buttons,
    axes,
    haptics: true,
    demo: true,
  };
}

function Logo() {
  return (
    <a className="brand" href="#top" aria-label="Simtile Gamepad Lab home">
      <span className="brand-mark" aria-hidden="true">
        <i />
        <i />
        <i />
        <i />
      </span>
      <span className="brand-name">SIMTILE</span>
      <span className="brand-product">GAMEPAD LAB</span>
    </a>
  );
}

function Header({
  connected,
  active,
  canInspect,
  onNavigate,
}: {
  connected: number;
  active: AppScreen;
  canInspect: boolean;
  onNavigate: (screen: AppScreen) => void;
}) {
  return (
    <header className="site-header">
      <Logo />
      <nav aria-label="Primary navigation">
        {(["live", "raw", "mapping"] as AppScreen[]).map((screen) => (
          <button
            className={active === screen ? "active" : ""}
            type="button"
            key={screen}
            disabled={screen !== "live" && !canInspect}
            onClick={() => onNavigate(screen)}
          >
            {screen === "live" ? "Live view" : screen === "raw" ? "Raw data" : "Mapping"}
          </button>
        ))}
      </nav>
      <div className={`connection-pill ${connected ? "is-connected" : ""}`}>
        <span />
        {connected ? `${connected} connected` : "Waiting for input"}
      </div>
    </header>
  );
}

function EmptyStage({ supported, onDemo }: { supported: boolean; onDemo: () => void }) {
  return (
    <div className="empty-stage">
      <div className="empty-pad" aria-hidden="true">
        <div className="empty-pad-dot left" />
        <div className="empty-pad-dot right" />
      </div>
      <p className="eyebrow">{supported ? "NO CONTROLLER DETECTED" : "GAMEPAD API UNAVAILABLE"}</p>
      <h2>{supported ? "Press any button to wake your gamepad." : "Try a current desktop browser."}</h2>
      <p>
        Connect by USB or Bluetooth, then press a face button. Some browsers only expose a controller after
        the first input.
      </p>
      <button className="text-button" type="button" onClick={onDemo}>
        Explore with a demo controller <span aria-hidden="true">→</span>
      </button>
    </div>
  );
}

function FaceButton({
  className,
  label,
  value,
  raw,
}: {
  className: string;
  label: string;
  value: number;
  raw?: number;
}) {
  return (
    <div
      className={`face-button ${className} ${value > 0.08 ? "is-active" : ""}`}
      style={{ "--input-value": value } as React.CSSProperties}
      title={`${label}${raw === undefined ? " · unmapped" : ` · raw b${raw}`} · ${value.toFixed(3)}`}
    >
      <span>{label}</span>
      <small>{raw === undefined ? "—" : `b${raw}`}</small>
    </div>
  );
}

function SmallButton({
  className,
  label,
  value,
  raw,
}: {
  className: string;
  label: string;
  value: number;
  raw?: number;
}) {
  return (
    <div
      className={`${className} small-control ${value > 0.08 ? "is-active" : ""}`}
      style={{ "--input-value": value } as React.CSSProperties}
      title={`${label}${raw === undefined ? " · unmapped" : ` · raw b${raw}`}`}
    >
      <span>{label}</span>
      <small>{raw === undefined ? "—" : `b${raw}`}</small>
    </div>
  );
}

function ControllerView({
  pad,
  profile,
  tuning,
  overrides,
  onPositionChange,
}: {
  pad: PadSnapshot;
  profile: MappingProfile;
  tuning: boolean;
  overrides: Partial<Record<VisualCluster, VisualPoint>>;
  onPositionChange: (cluster: VisualCluster, point: VisualPoint) => void;
}) {
  const [dragState, setDragState] = useState<{
    cluster: VisualCluster;
    startX: number;
    startY: number;
    startPoint: VisualPoint;
    width: number;
    height: number;
  } | null>(null);
  const button = (key: ButtonKey) => mappedButtonValue(pad.buttons, profile, key);
  const raw = (key: ButtonKey) => profile.buttons[key];
  const axis = (key: AxisKey) => mappedAxisValue(pad.axes, profile, key);
  const labels = {
    xbox: ["A", "B", "X", "Y"],
    playstation: ["×", "○", "□", "△"],
    "8bitdo-ultimate-2": ["A", "B", "X", "Y"],
    "8bitdo-pro-3": ["B", "A", "Y", "X"],
    "8bitdo": ["B", "A", "Y", "X"],
    generic: ["1", "2", "3", "4"],
  }[profile.layout];
  const visualPreset = CONTROLLER_VISUAL_PRESETS[profile.layout] ?? CONTROLLER_VISUAL_PRESETS.generic;
  const position = (cluster: VisualCluster) => overrides[cluster] ?? visualPreset.positions[cluster];
  const positionStyle = (cluster: VisualCluster): React.CSSProperties => ({
    left: `${position(cluster).x}%`,
    right: "auto",
    top: `${position(cluster).y}%`,
    bottom: "auto",
  });

  const beginDrag = (cluster: VisualCluster, event: React.PointerEvent<HTMLElement>) => {
    if (!tuning) return;
    const shell = event.currentTarget.closest<HTMLElement>(".controller-shell");
    if (!shell) return;
    const bounds = shell.getBoundingClientRect();
    setDragState({
      cluster,
      startX: event.clientX,
      startY: event.clientY,
      startPoint: position(cluster),
      width: bounds.width,
      height: bounds.height,
    });
    event.currentTarget.setPointerCapture(event.pointerId);
    event.preventDefault();
  };

  const moveDrag = (event: React.PointerEvent<HTMLElement>) => {
    const drag = dragState;
    if (!drag) return;
    const x = drag.startPoint.x + ((event.clientX - drag.startX) / drag.width) * 100;
    const y = drag.startPoint.y + ((event.clientY - drag.startY) / drag.height) * 100;
    onPositionChange(drag.cluster, {
      x: Math.max(2, Math.min(85, Number(x.toFixed(2)))),
      y: Math.max(-2, Math.min(88, Number(y.toFixed(2)))),
    });
  };

  const endDrag = () => {
    setDragState(null);
  };

  const draggable = (cluster: VisualCluster) => ({
    className: tuning ? "visual-cluster is-tuneable" : "visual-cluster",
    style: positionStyle(cluster),
    onPointerDown: (event: React.PointerEvent<HTMLElement>) => beginDrag(cluster, event),
    onPointerMove: moveDrag,
    onPointerUp: endDrag,
    onPointerCancel: endDrag,
  });

  return (
    <div className={`controller controller-${profile.layout} controller-mode-schematic ${tuning ? "is-tuning" : ""}`}>
      <div className="controller-shadow" />
      <div className="shoulder shoulder-left trigger">
        <SmallButton className="trigger-control" label="LT" value={button("leftTrigger")} raw={raw("leftTrigger")} />
      </div>
      <div className="shoulder shoulder-right trigger">
        <SmallButton className="trigger-control" label="RT" value={button("rightTrigger")} raw={raw("rightTrigger")} />
      </div>
      <div className="shoulder shoulder-left bumper">
        <SmallButton className="bumper-control" label="LB" value={button("leftBumper")} raw={raw("leftBumper")} />
      </div>
      <div className="shoulder shoulder-right bumper">
        <SmallButton className="bumper-control" label="RB" value={button("rightBumper")} raw={raw("rightBumper")} />
      </div>
      <div className="controller-shell">
        <div className={`schematic-body schematic-body-${visualPreset.body}`} aria-hidden="true">
          <div className="schematic-body-panel" />
          <div className="schematic-body-mark">SIMTILE / {visualPreset.name}</div>
        </div>

        <div {...draggable("leftStick")} data-cluster="LEFT STICK">
          <div className="stick stick-left">
          <div
            className="stick-cap"
            style={{
              transform: `translate(${axis("leftStickX") * 10}px, ${axis("leftStickY") * 10}px)`,
            }}
          >
            <span>L3</span>
            <small>{raw("leftStick") === undefined ? "—" : `b${raw("leftStick")}`}</small>
          </div>
          <div className="axis-readout">
            {axis("leftStickX").toFixed(2)} · {axis("leftStickY").toFixed(2)}
          </div>
          </div>
        </div>

        <div {...draggable("rightStick")} data-cluster="RIGHT STICK">
          <div className="stick stick-right">
          <div
            className="stick-cap"
            style={{
              transform: `translate(${axis("rightStickX") * 10}px, ${axis("rightStickY") * 10}px)`,
            }}
          >
            <span>R3</span>
            <small>{raw("rightStick") === undefined ? "—" : `b${raw("rightStick")}`}</small>
          </div>
          <div className="axis-readout">
            {axis("rightStickX").toFixed(2)} · {axis("rightStickY").toFixed(2)}
          </div>
          </div>
        </div>

        <div {...draggable("dpad")} data-cluster="D-PAD">
          <div className="dpad" aria-label="D-pad status">
          <SmallButton className="dpad-up" label="↑" value={button("dpadUp")} raw={raw("dpadUp")} />
          <SmallButton className="dpad-right" label="→" value={button("dpadRight")} raw={raw("dpadRight")} />
          <SmallButton className="dpad-down" label="↓" value={button("dpadDown")} raw={raw("dpadDown")} />
          <SmallButton className="dpad-left" label="←" value={button("dpadLeft")} raw={raw("dpadLeft")} />
          <div className="dpad-center" />
          </div>
        </div>

        <div {...draggable("face")} data-cluster="FACE BUTTONS">
          <div className="face-cluster" aria-label="Face button status">
          <FaceButton className="face-south" label={labels[0]} value={button("south")} raw={raw("south")} />
          <FaceButton className="face-east" label={labels[1]} value={button("east")} raw={raw("east")} />
          <FaceButton className="face-west" label={labels[2]} value={button("west")} raw={raw("west")} />
          <FaceButton className="face-north" label={labels[3]} value={button("north")} raw={raw("north")} />
          </div>
        </div>

        <div {...draggable("system")} data-cluster="SYSTEM">
          <div className="system-buttons">
          <SmallButton className="system-control" label="−" value={button("select")} raw={raw("select")} />
          <SmallButton className="home-control" label="S" value={button("home")} raw={raw("home")} />
          <SmallButton className="system-control" label="+" value={button("start")} raw={raw("start")} />
          </div>
        </div>

        <div {...draggable("rear")} data-cluster="EXTRA BUTTONS">
          <div className="rear-controls">
          {(["leftRear", "leftPaddle", "rightPaddle", "rightRear"] as ButtonKey[]).map((key) => {
            const definition = BUTTON_CONTROLS.find((item) => item.key === key)!;
            return (
              <SmallButton
                key={key}
                className="rear-control"
                label={definition.short}
                value={button(key)}
                raw={raw(key)}
              />
            );
          })}
          </div>
        </div>
      </div>
    </div>
  );
}

function DeviceFacts({ pad, profile, onHaptics }: { pad: PadSnapshot; profile: MappingProfile; onHaptics: () => void }) {
  const mappedCount = Object.values(profile.buttons).filter((value) => value !== undefined).length;
  const extraButtons = Math.max(0, pad.buttons.length - 17);
  const artwork = CONTROLLER_ARTWORK[profile.layout] ?? CONTROLLER_ARTWORK.generic;
  return (
    <aside className="device-facts">
      <div className="section-label"><span>DEVICE REPORT</span><b>LIVE</b></div>
      <a className={`device-reference controller-${profile.layout}`} href={artwork.source} target="_blank" rel="noreferrer">
        <span className="device-reference-art" aria-hidden="true"><img src={artwork.src} alt="" /></span>
        <span className="device-reference-copy"><small>PHOTO REFERENCE</small><b>{artwork.name}</b></span>
      </a>
      <dl>
        <div>
          <dt>Browser mapping</dt>
          <dd className={pad.mapping === "standard" ? "success" : "warning"}>
            {pad.mapping || "unknown"}
          </dd>
        </div>
        <div>
          <dt>Raw inputs</dt>
          <dd>{pad.buttons.length} buttons · {pad.axes.length} axes</dd>
        </div>
        <div>
          <dt>Profile coverage</dt>
          <dd>{mappedCount} / {BUTTON_CONTROLS.length} controls</dd>
        </div>
        <div>
          <dt>Extra buttons</dt>
          <dd className={extraButtons ? "accent" : ""}>{extraButtons || "None reported"}</dd>
        </div>
        <div>
          <dt>Haptics</dt>
          <dd>{pad.haptics ? "Available" : "Not reported"}</dd>
        </div>
      </dl>
      <button className="secondary-button full-width" type="button" onClick={onHaptics} disabled={!pad.haptics || pad.demo}>
        <span className="button-icon" aria-hidden="true">≋</span>
        Test vibration
      </button>
      <p className="privacy-note"><span aria-hidden="true">●</span> Inputs stay in this browser.</p>
    </aside>
  );
}

function RawInspector({ pad, events }: { pad: PadSnapshot; events: InputEventItem[] }) {
  const [onlyActive, setOnlyActive] = useState(false);
  const buttons = onlyActive ? pad.buttons.map((button, index) => ({ ...button, index })).filter((b) => b.value > 0.02) : pad.buttons.map((button, index) => ({ ...button, index }));

  return (
    <section className="section-block" id="raw">
      <div className="section-heading">
        <div>
          <p className="eyebrow">RAW INPUT MONITOR</p>
          <h2>Every signal, exactly as the browser sees it.</h2>
        </div>
        <label className="toggle-control">
          <input type="checkbox" checked={onlyActive} onChange={(event) => setOnlyActive(event.target.checked)} />
          <span />
          Active only
        </label>
      </div>

      <div className="inspector-grid">
        <div className="panel raw-panel">
          <div className="panel-heading">
            <h3>Buttons</h3>
            <span>{pad.buttons.length} channels</span>
          </div>
          <div className="button-matrix">
            {buttons.length ? buttons.map((button) => (
              <div
                className={`raw-button ${button.value > 0.02 ? "is-active" : ""}`}
                key={button.index}
                style={{ "--input-value": button.value } as React.CSSProperties}
              >
                <div><b>b{button.index}</b><span>{button.pressed ? "PRESSED" : button.touched ? "TOUCHED" : "IDLE"}</span></div>
                <strong>{button.value.toFixed(3)}</strong>
                <i><span style={{ width: `${button.value * 100}%` }} /></i>
              </div>
            )) : <p className="muted-empty">Move or press an input to reveal it.</p>}
          </div>
        </div>

        <div className="inspector-side">
          <div className="panel axes-panel">
            <div className="panel-heading">
              <h3>Axes</h3>
              <span>−1.000 → 1.000</span>
            </div>
            <div className="axis-list">
              {pad.axes.map((value, index) => (
                <div className="raw-axis" key={index}>
                  <div><b>a{index}</b><strong>{value.toFixed(3)}</strong></div>
                  <div className="axis-track">
                    <i />
                    <span style={{ left: `${((value + 1) / 2) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="panel event-panel">
            <div className="panel-heading">
              <h3>Event log</h3>
              <span>Newest first</span>
            </div>
            <div className="event-list" aria-live="polite">
              {events.length ? events.map((item) => (
                <div className="event-row" key={item.id}>
                  <time>{item.time}</time>
                  <b>{item.input}</b>
                  <span>{item.action}</span>
                  <strong>{item.value}</strong>
                </div>
              )) : <p className="muted-empty">Input changes will appear here.</p>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function MappingStudio({
  pad,
  profile,
  onChange,
  onSave,
  onExport,
  onCopy,
  onImport,
  saved,
  capture,
  onCapture,
  notice,
}: {
  pad: PadSnapshot;
  profile: MappingProfile;
  onChange: (profile: MappingProfile) => void;
  onSave: () => void;
  onExport: () => void;
  onCopy: () => void;
  onImport: (file: File) => void;
  saved: boolean;
  capture: CaptureTarget | null;
  onCapture: (target: CaptureTarget) => void;
  notice: string;
}) {
  const [tab, setTab] = useState<"buttons" | "axes">("buttons");

  const updateButton = (key: ButtonKey, rawValue: string) => {
    const buttons = { ...profile.buttons };
    if (rawValue === "") delete buttons[key];
    else buttons[key] = Number(rawValue);
    onChange({ ...profile, buttons });
  };

  const updateAxis = (key: AxisKey, rawValue: string) => {
    const axes = { ...profile.axes };
    if (rawValue === "") delete axes[key];
    else axes[key] = { ...axes[key], index: Number(rawValue) };
    onChange({ ...profile, axes });
  };

  const invertAxis = (key: AxisKey) => {
    const binding = profile.axes[key];
    if (!binding) return;
    onChange({ ...profile, axes: { ...profile.axes, [key]: { ...binding, invert: !binding.invert } } });
  };

  return (
    <section className="section-block mapping-section" id="mapping">
      <div className="section-heading mapping-heading">
        <div>
          <p className="eyebrow">MAPPING STUDIO</p>
          <h2>Teach the browser a controller it doesn’t know.</h2>
        </div>
        <div className="mapping-actions">
          <label className="secondary-button file-button">
            Import
            <input
              type="file"
              accept="application/json,.json"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) onImport(file);
                event.target.value = "";
              }}
            />
          </label>
          <button className="secondary-button" type="button" onClick={onCopy}>Copy JSON</button>
          <button className="secondary-button" type="button" onClick={onExport}>Export</button>
          <button className="primary-button" type="button" onClick={onSave}>Save profile</button>
        </div>
      </div>

      <div className="mapping-workspace">
        <aside className="profile-panel panel">
          <div className="panel-heading">
            <h3>Profile</h3>
            <span className={saved ? "saved-state" : "draft-state"}>{saved ? "SAVED" : "DRAFT"}</span>
          </div>
          <label>
            Profile name
            <input value={profile.name} onChange={(event) => onChange({ ...profile, name: event.target.value })} />
          </label>
          <label>
            Match exact device ID
            <textarea
              rows={3}
              value={profile.match.id}
              onChange={(event) => onChange({ ...profile, match: { ...profile.match, id: event.target.value } })}
            />
          </label>
          <label>
            Controller form
            <select
              value={profile.layout}
              onChange={(event) => onChange({ ...profile, layout: event.target.value as ControllerLayout })}
            >
              <option value="8bitdo-ultimate-2">8BitDo Ultimate 2</option>
              <option value="8bitdo-pro-3">8BitDo Pro 3</option>
              <option value="8bitdo">8BitDo generic</option>
              <option value="xbox">Xbox</option>
              <option value="playstation">PlayStation</option>
              <option value="generic">Generic</option>
            </select>
          </label>
          <label>
            Stick deadzone <strong>{profile.deadzone.toFixed(2)}</strong>
            <input
              type="range"
              min="0"
              max="0.35"
              step="0.01"
              value={profile.deadzone}
              onChange={(event) => onChange({ ...profile, deadzone: Number(event.target.value) })}
            />
          </label>
          <label>
            Notes
            <textarea
              rows={3}
              placeholder="Firmware, connection mode, quirks…"
              value={profile.notes ?? ""}
              onChange={(event) => onChange({ ...profile, notes: event.target.value })}
            />
          </label>
          <div className="profile-meta">
            <span>Schema</span><code>simtile.gamepad-mapping/v1</code>
            <span>Browser map</span><code>{pad.mapping || "unknown"}</code>
          </div>
        </aside>

        <div className="bindings-panel panel">
          <div className="binding-tabs" role="tablist" aria-label="Mapping input type">
            <button className={tab === "buttons" ? "active" : ""} type="button" onClick={() => setTab("buttons")}>
              Buttons <span>{Object.keys(profile.buttons).length}/{BUTTON_CONTROLS.length}</span>
            </button>
            <button className={tab === "axes" ? "active" : ""} type="button" onClick={() => setTab("axes")}>
              Axes <span>{Object.keys(profile.axes).length}/{AXIS_CONTROLS.length}</span>
            </button>
          </div>
          <div className="binding-help">
            <span className={capture ? "capture-dot recording" : "capture-dot"} />
            {capture ? "Now press or move the requested control…" : "Choose Learn, then press or move that control once."}
          </div>

          {tab === "buttons" ? (
            <div className="binding-list">
              {BUTTON_CONTROLS.map((control) => {
                const rawIndex = profile.buttons[control.key];
                const value = rawIndex === undefined ? 0 : (pad.buttons[rawIndex]?.value ?? 0);
                const isCapturing = capture?.kind === "button" && capture.key === control.key;
                return (
                  <div className={`binding-row ${value > 0.02 ? "is-active" : ""} ${isCapturing ? "is-capturing" : ""}`} key={control.key}>
                    <span className="semantic-key">{control.short}</span>
                    <div><b>{control.label}</b><small>{control.group}</small></div>
                    <select value={rawIndex ?? ""} onChange={(event) => updateButton(control.key, event.target.value)} aria-label={`Raw input for ${control.label}`}>
                      <option value="">Unmapped</option>
                      {pad.buttons.map((_, index) => <option value={index} key={index}>b{index}</option>)}
                    </select>
                    <output>{value.toFixed(3)}</output>
                    <button className="learn-button" type="button" onClick={() => onCapture({ kind: "button", key: control.key })}>
                      {isCapturing ? "Listening…" : "Learn"}
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="binding-list">
              {AXIS_CONTROLS.map((control) => {
                const binding = profile.axes[control.key];
                const rawValue = binding ? (pad.axes[binding.index] ?? 0) * (binding.invert ? -1 : 1) : 0;
                const isCapturing = capture?.kind === "axis" && capture.key === control.key;
                return (
                  <div className={`binding-row axis-binding ${Math.abs(rawValue) > 0.08 ? "is-active" : ""} ${isCapturing ? "is-capturing" : ""}`} key={control.key}>
                    <span className="semantic-key">{control.short}</span>
                    <div><b>{control.label}</b><small>stick axis</small></div>
                    <select value={binding?.index ?? ""} onChange={(event) => updateAxis(control.key, event.target.value)} aria-label={`Raw axis for ${control.label}`}>
                      <option value="">Unmapped</option>
                      {pad.axes.map((_, index) => <option value={index} key={index}>a{index}</option>)}
                    </select>
                    <output>{rawValue.toFixed(3)}</output>
                    <button className={`invert-button ${binding?.invert ? "active" : ""}`} type="button" onClick={() => invertAxis(control.key)} disabled={!binding}>
                      ± Invert
                    </button>
                    <button className="learn-button" type="button" onClick={() => onCapture({ kind: "axis", key: control.key })}>
                      {isCapturing ? "Listening…" : "Learn"}
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div className={`toast ${notice ? "show" : ""}`} role="status">{notice}</div>
    </section>
  );
}

function App() {
  const { gamepads, supported } = useGamepads();
  const [demoActive, setDemoActive] = useState(false);
  const demoPad = useDemoPad(demoActive && gamepads.length === 0);
  const availablePads = useMemo(() => demoPad ? [demoPad] : gamepads, [demoPad, gamepads]);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [profiles, setProfiles] = useState<MappingProfile[]>(() => loadProfiles());
  const [draft, setDraft] = useState<MappingProfile | null>(null);
  const [capture, setCapture] = useState<CaptureTarget | null>(null);
  const [events, setEvents] = useState<InputEventItem[]>([]);
  const [notice, setNotice] = useState("");
  const [screen, setScreen] = useState<AppScreen>("live");
  const [layoutTuning, setLayoutTuning] = useState(false);
  const [visualLayouts, setVisualLayouts] = useState<VisualLayoutOverrides>(() => loadVisualLayoutOverrides());
  const previousRef = useRef<PadSnapshot | null>(null);
  const captureBaseline = useRef<{ buttons: number[]; axes: number[] } | null>(null);
  const eventId = useRef(0);

  const selected = availablePads.find((pad) => pad.index === selectedIndex) ?? availablePads[0] ?? null;

  useEffect(() => {
    if (availablePads.length === 0) {
      setSelectedIndex(null);
      setScreen("live");
      return;
    }
    if (!availablePads.some((pad) => pad.index === selectedIndex)) setSelectedIndex(availablePads[0].index);
  }, [availablePads, selectedIndex]);

  useEffect(() => {
    if (!selected) {
      setDraft(null);
      return;
    }
    const stored = resolveProfile(profiles, selected.id);
    setDraft(stored ? cloneProfile(stored) : createProfile(selected));
    setCapture(null);
    previousRef.current = null;
    setEvents([]);
  }, [selected?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!selected) return;
    const previous = previousRef.current;
    if (previous?.id === selected.id) {
      const additions: InputEventItem[] = [];
      selected.buttons.forEach((button, index) => {
        const old = previous.buttons[index];
        if (!old || old.pressed === button.pressed) return;
        additions.push({
          id: ++eventId.current,
          time: new Date().toLocaleTimeString([], { hour12: false }),
          input: `b${index}`,
          action: button.pressed ? "pressed" : "released",
          value: button.value.toFixed(3),
        });
      });
      selected.axes.forEach((value, index) => {
        const old = previous.axes[index] ?? 0;
        if (Math.abs(value) > 0.72 && Math.abs(old) <= 0.72) {
          additions.push({
            id: ++eventId.current,
            time: new Date().toLocaleTimeString([], { hour12: false }),
            input: `a${index}`,
            action: value > 0 ? "positive" : "negative",
            value: value.toFixed(3),
          });
        }
      });
      if (additions.length) setEvents((items) => [...additions.reverse(), ...items].slice(0, 9));
    }
    previousRef.current = selected;
  }, [selected]);

  useEffect(() => {
    if (!capture || !selected || !draft || !captureBaseline.current) return;
    const baseline = captureBaseline.current;
    if (capture.kind === "button") {
      const candidate = selected.buttons
        .map((button, index) => ({ index, delta: button.value - (baseline.buttons[index] ?? 0), value: button.value }))
        .filter((item) => item.value > 0.45 && item.delta > 0.35)
        .sort((a, b) => b.delta - a.delta)[0];
      if (candidate) {
        setDraft({ ...draft, buttons: { ...draft.buttons, [capture.key]: candidate.index } });
        setCapture(null);
        setNotice(`${BUTTON_CONTROLS.find((item) => item.key === capture.key)?.label} mapped to b${candidate.index}`);
      }
    } else {
      const candidate = selected.axes
        .map((value, index) => ({ index, delta: Math.abs(value - (baseline.axes[index] ?? 0)), value }))
        .filter((item) => item.delta > 0.55)
        .sort((a, b) => b.delta - a.delta)[0];
      if (candidate) {
        setDraft({
          ...draft,
          axes: { ...draft.axes, [capture.key]: { index: candidate.index, invert: candidate.value < 0 } },
        });
        setCapture(null);
        setNotice(`${AXIS_CONTROLS.find((item) => item.key === capture.key)?.label} mapped to a${candidate.index}`);
      }
    }
  }, [capture, draft, selected]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(""), 2600);
    return () => window.clearTimeout(timer);
  }, [notice]);

  useEffect(() => {
    saveVisualLayoutOverrides(visualLayouts);
  }, [visualLayouts]);

  const beginCapture = useCallback((target: CaptureTarget) => {
    if (!selected) return;
    captureBaseline.current = {
      buttons: selected.buttons.map((button) => button.value),
      axes: [...selected.axes],
    };
    setCapture(target);
  }, [selected]);

  const saveDraft = () => {
    if (!draft || !selected) return;
    const now = new Date().toISOString();
    const nextProfile = { ...draft, updatedAt: now };
    const next = [...profiles.filter((profile) => profile.match.id !== nextProfile.match.id), nextProfile];
    setProfiles(next);
    saveProfiles(next);
    setDraft(nextProfile);
    setNotice("Profile saved locally");
  };

  const exportDraft = () => {
    if (!draft) return;
    const blob = new Blob([JSON.stringify(draft, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `${draft.profileId}.gamepad.json`;
    anchor.click();
    URL.revokeObjectURL(url);
    setNotice("Profile exported");
  };

  const copyDraft = async () => {
    if (!draft) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(draft, null, 2));
      setNotice("JSON copied to clipboard");
    } catch {
      setNotice("Clipboard permission was denied");
    }
  };

  const importProfile = async (file: File) => {
    try {
      const imported = JSON.parse(await file.text()) as MappingProfile;
      if (imported.schema !== "simtile.gamepad-mapping/v1" || !imported.match?.id) throw new Error("Invalid profile");
      setDraft(cloneProfile(imported));
      setNotice("Profile imported as a draft");
    } catch {
      setNotice("That file is not a valid Simtile profile");
    }
  };

  const testHaptics = async () => {
    if (!selected || selected.demo) return;
    type RumblePad = Gamepad & {
      vibrationActuator?: { playEffect: (type: "dual-rumble", params: Record<string, number>) => Promise<unknown> };
    };
    const gamepad = navigator.getGamepads()[selected.index] as RumblePad | null;
    try {
      await gamepad?.vibrationActuator?.playEffect("dual-rumble", {
        duration: 260,
        startDelay: 0,
        strongMagnitude: 0.85,
        weakMagnitude: 0.45,
      });
      setNotice("Vibration pulse sent");
    } catch {
      setNotice("This browser blocked the vibration test");
    }
  };

  const storedForSelected = selected ? resolveProfile(profiles, selected.id) : undefined;
  const isSaved = Boolean(storedForSelected && draft && JSON.stringify(storedForSelected) === JSON.stringify(draft));
  const updateVisualPosition = (cluster: VisualCluster, point: VisualPoint) => {
    if (!draft) return;
    setVisualLayouts((current) => ({
      ...current,
      [draft.layout]: {
        ...current[draft.layout],
        [cluster]: point,
      },
    }));
  };

  const resetVisualLayout = () => {
    if (!draft) return;
    setVisualLayouts((current) => {
      const next = { ...current };
      delete next[draft.layout];
      return next;
    });
    setNotice("Visual layout reset to its model preset");
  };

  return (
    <div id="top">
      <Header
        connected={gamepads.length}
        active={selected && draft ? screen : "live"}
        canInspect={Boolean(selected && draft)}
        onNavigate={setScreen}
      />
      <main>
        {screen === "live" || !selected || !draft ? (
          <section className="live-panel" id="live">
            {selected && draft ? (
              <>
                <div className="device-bar">
                  <div className="device-identity">
                    <span className="device-index">{selected.demo ? "DEMO" : `0${selected.index + 1}`}</span>
                    <div>
                      <p>{selected.demo ? "PREVIEW DEVICE" : "CONNECTED DEVICE"}</p>
                      <h2 title={selected.id}>{selected.id}</h2>
                    </div>
                  </div>
                  <div className="device-controls">
                    {availablePads.length > 1 && (
                      <select value={selected.index} onChange={(event) => setSelectedIndex(Number(event.target.value))} aria-label="Select connected gamepad">
                        {availablePads.map((pad) => <option value={pad.index} key={pad.index}>{pad.index + 1} · {pad.id}</option>)}
                      </select>
                    )}
                    {selected.demo && <button className="secondary-button" type="button" onClick={() => setDemoActive(false)}>Exit demo</button>}
                    <span className="polling-status"><i /> POLLING 30 FPS</span>
                  </div>
                </div>
                <div className="live-grid">
                  <div className="controller-stage">
                    <div className="controller-view-tools" aria-label="Controller view options">
                      <span>SCHEMATIC · {CONTROLLER_VISUAL_PRESETS[draft.layout].name}</span>
                      <button
                        className={`layout-tune-button ${layoutTuning ? "active" : ""}`}
                        type="button"
                        aria-pressed={layoutTuning}
                        onClick={() => setLayoutTuning((value) => !value)}
                      >
                        {layoutTuning ? "Done" : "Tune layout"}
                      </button>
                      {layoutTuning && (
                        <button className="layout-reset-button" type="button" onClick={resetVisualLayout}>
                          Reset
                        </button>
                      )}
                    </div>
                    {layoutTuning && <p className="layout-tune-hint">Drag a control group · saved on this device</p>}
                    <ControllerView
                      pad={selected}
                      profile={draft}
                      tuning={layoutTuning}
                      overrides={visualLayouts[draft.layout] ?? {}}
                      onPositionChange={updateVisualPosition}
                    />
                  </div>
                  <DeviceFacts pad={selected} profile={draft} onHaptics={testHaptics} />
                </div>
              </>
            ) : <EmptyStage supported={supported} onDemo={() => setDemoActive(true)} />}
          </section>
        ) : screen === "raw" ? (
          <RawInspector pad={selected} events={events} />
        ) : (
          <MappingStudio
            pad={selected}
            profile={draft}
            onChange={setDraft}
            onSave={saveDraft}
            onExport={exportDraft}
            onCopy={copyDraft}
            onImport={importProfile}
            saved={isSaved}
            capture={capture}
            onCapture={beginCapture}
            notice={notice}
          />
        )}
      </main>
    </div>
  );
}

export default App;
